import React from "react";
import SVG from "react-inlinesvg";
import styled from "styled-components/macro";
import { buttonReset } from "../../styles/a11y";

interface TextButtonProps {
  src?: string;
  size?: "small" | "medium" | "large";
  iconSize?: number;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  ariaLabel?: string;
}

const TextButtonWrapper = styled.button`
  ${buttonReset}
  &:focus,
  &:focus-visible {
    outline: none;
  }
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  white-space: nowrap;
  border: none !important;
  &:hover {
    & > div {
      border-radius: 8px;
      background-color: var(--menu-color-fullscreen-hover);
    }
  }

  &:active {
    & > div {
      background-color: var(--menu-color-active);
    }
  }

  &.disabled {
    pointer-events: none;
    opacity: 0.5;
  }
`;

const Inner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.2s ease-in-out;
  border-radius: 30px;
  font-weight: 500;
  gap: 3px;
  font-size: 16px;
  padding: 8px 10px;
  color: var(--grey-0);

  ${TextButtonWrapper}:focus-visible > & {
    outline: 2px solid var(--primary-cta);
    outline-offset: 2px;
    border-radius: 8px;
  }
`;

const StyledSVG = styled(SVG)`
  width: ${(props: { width: string }) => props.width};
  height: ${(props: { height: string }) => props.height};
  fill: currentColor;
  path {
    fill: var(--grey-0);
  }
`;

const TextButton = React.forwardRef<HTMLButtonElement, TextButtonProps>(({ src, size, iconSize, onClick, className, children, disabled, ariaLabel }, ref) => {
  const iconSizePx = `${iconSize ?? 24}px`;

  return (
    <TextButtonWrapper
      ref={ref}
      type="button"
      size={size}
      onClick={onClick}
      disabled={disabled}
      className={`text-button-wrapper use-component-styles ${className ?? ""} ${disabled ? "disabled" : ""}`}
      aria-label={ariaLabel}
    >
      <Inner className="text-button-inner">
        {src && (
          <StyledSVG
            src={src}
            width={iconSizePx}
            height={iconSizePx}
            aria-hidden="true"
          />
        )}
        {children}
      </Inner>
    </TextButtonWrapper>
  );
});

export default TextButton;
